"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "@/components/ui/CardImage";
import Link from "@/components/ui/Link";
import ZodiacSign, { ZodiacSignName } from "../ui/ZodiacSign";
import { ChevronRight, ArrowDown } from "lucide-react";
import { PanchangamEntry } from "@/lib/api";
import { getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

const zodiacSigns: { id: ZodiacSignName; apiKey: string }[] = [
  { id: "mesham", apiKey: "MeshamRasiPalan" },
  { id: "rishabam", apiKey: "RishabamRasiPalan" },
  { id: "mithunam", apiKey: "MidhunamRasiPalan" },
  { id: "kadagam", apiKey: "KadahamRasiPalan" },
  { id: "simmam", apiKey: "SimamRasiPalan" },
  { id: "kanni", apiKey: "KanniRasiPalan" },
  { id: "thulam", apiKey: "ThulamRasiPalan" },
  { id: "viruchigam", apiKey: "ViruchagammRasiPalan" },
  { id: "dhanusu", apiKey: "DanushuRasiPalan" },
  { id: "magaram", apiKey: "MagaramRasiPalan" },
  { id: "kumbam", apiKey: "KumbamRasiPalan" },
  { id: "meenam", apiKey: "MenamRasiPalan" },
];

type AstrologySectionProps = {
  weeklyImage?: string;
  monthlyImage?: string;
  weeklySlug?: string;
  monthlySlug?: string;
  className?: string;
  imageMetadata?: ImageMetadata;
};

const Astrology = ({
  weeklyImage = "https://media.dinamani.com/dinamani/2026-02-27/5u7t1tzo/weekly.png",
  monthlyImage = "https://media.dinamani.com/dinamani/2026-02-27/og31pqoi/monthly.png",
  weeklySlug = "astrology/astro-predictions/weekly-predictions",
  monthlySlug = "astrology/astro-predictions/monthly-predictions",
  className = "",
  imageMetadata,
}: AstrologySectionProps) => {
  const [panchangam, setPanchangam] = useState<PanchangamEntry | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadPanchangam() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch("/api/panchangam", {
          method: "GET",
          headers: { "Accept": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!isMounted) return;
        if (data && Array.isArray(data) && data.length > 0) {
          setPanchangam(data[0]);
        } else {
          setError("தகவல் இல்லை");
          setPanchangam(null);
        }
      } catch (err) {
        console.error("[AstrologySection] Unable to fetch panchangam data:", err);
        if (isMounted) {
          setError("இணைப்பில் சிக்கல். பின்னர் முயற்சிக்கவும்.");
          setPanchangam(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPanchangam();
    return () => {
      isMounted = false;
    };
  }, []);

  const auspiciousTimes = useMemo(
    () => [
      {
        label: "காலை",
        icon: "https://media.dinamani.com/dinamani/2026-02-27/ltmvh0xc/sun1.png",
        value: panchangam?.NallaNeramKalai ?? "—",
      },
      {
        label: "மாலை",
        icon: "https://media.dinamani.com/dinamani/2026-02-27/eqbck4lj/sun2.png",
        value: panchangam?.NallaNeramMalai ?? "—",
      },
    ],
    [panchangam]
  );

  const inauspiciousTimes = useMemo(
    () => [
      {
        label: "ராகு காலம்",
        value: panchangam?.RaaguKaalam ?? "—",
      },
      {
        label: "எமகண்டம்",
        value: panchangam?.Yemmakandam ?? "—",
      },
      {
        label: "குளிகை",
        value: panchangam?.Kuligai ?? "—",
      },
    ],
    [panchangam]
  );

  return (
    <section className={`w-full relative overflow-hidden shadow-lg ${className}`}>
      {/* Full Width Background Image */}
      <div className="absolute inset-0 -z-10">
        <Image
          src="https://media.dinamani.com/dinamani/2026-02-27/wok2tovm/astrologybg.png"
          alt="Astrology Background"
          fill
          className="object-cover"
          style={getFocusPointStyle(imageMetadata)}
          loading="lazy"
          placeholder="blur"
          blurDataURL={BLUR_PLACEHOLDER}
        />
      </div>

      <div className="relative z-10">
        {/* Header - Now Full Width */}
        <div className="px-3 sm:px-4 pt-3 sm:pt-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-FFFFFF text-18-tamil-700 flex items-center gap-2">
              {/* <ArrowDown className="w-4 h-4" /> */}
              பஞ்சாங்கம் - ராசிபலன்
            </h2>
            <Link href="/astrology" className="flex items-center text-xs sm:text-sm hover:opacity-80 transition-opacity">
              <span className="text-FFFFFF text-12-tamil-600 transition-colors">மேலும்</span>
              {/* <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" /> */}
            </Link>
          </div>
        </div>

        <div className="flex flex-col xxl:flex-row gap-0">
          {/* Left Side - Astrology/Panchangam Section */}
          <div className="flex-1 text-FFFFFF">
            <div className="flex flex-col justify-around h-full">
              {/* Cards Container */}
              <div className="relative z-10 px-2 sm:px-4 py-4 grid grid-cols-1 md:grid-cols-3 gap-2.5 sm:gap-3 md:gap-4">
                {/* First Card - Zodiac Signs */}
                <div className="flex items-center justify-center w-full min-w-0">
                  <div className="grid grid-cols-4 gap-3 sm:gap-4 w-full">
                    {zodiacSigns.map((sign) => {
                      const rasiPalan = panchangam?.[sign.apiKey as keyof PanchangamEntry] as string | undefined;
                      return (
                        <div key={sign.id} className="flex flex-col items-center justify-center min-w-0">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-14 md:h-14 flex items-center justify-center">
                            <ZodiacSign
                              sign={sign.id}
                              size={56}
                              className="w-full h-full"
                            />
                          </div>
                          <span className="text-FFFFFF text-10-tamil-700 sm:text-12-tamil-700 text-center mt-3 sm:mt-4 leading-tight">
                            {rasiPalan || "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Second Card - Date */}
                <div className="flex flex-col justify-center bg-[#2D497E]/40 backdrop-blur-sm px-4 py-6">
                  {loading ? (
                    <div className="animate-pulse space-y-4 w-full">
                      {/* Date Skeleton */}
                      <div className="h-6 bg-white/20 rounded w-3/4"></div>
                      {/* Details Skeleton */}
                      <div className="h-4 bg-white/20 rounded w-full"></div>

                      <div className="mt-4">
                        {/* Title Skeleton */}
                        <div className="h-5 bg-white/20 rounded w-1/2 mb-4"></div>
                        {/* Times List Skeleton */}
                        <div className="space-y-3">
                          {[1, 2].map((i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-8 h-6 bg-white/20 rounded flex-shrink-0"></div>
                              <div className="flex flex-col gap-1 w-full">
                                <div className="h-3 bg-white/20 rounded w-1/4"></div>
                                <div className="h-3 bg-white/20 rounded w-1/2"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="my-2 sm:my-3 space-y-2">
                      <h3 className="text-14-tamil-700 text-FFFFFF">
                        {panchangam?.Panchangam_date
                          ? new Date(panchangam.Panchangam_date).toLocaleDateString("ta-IN", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            weekday: "long",
                          })
                          : "—"}
                      </h3>
                      <p className="text-FFFFFF text-12-tamil-700">
                        {`திதி: ${panchangam?.Thithi ?? "—"} | நட்சத்திரம்: ${panchangam?.Natchatram ?? "—"}`}
                      </p>

                      <div className="mt-3 sm:mt-4">
                        <h4 className="text-FFDB59 text-14-tamil-700 mb-4 sm:mb-4">சிறந்த நேரம்</h4>
                        <div className="space-y-1.5 sm:space-y-2">
                          {auspiciousTimes.map((item) => (
                            <div className="flex items-center gap-2" key={item.label}>
                              <Image
                                src={item.icon}
                                alt={item.label}
                                width={30}
                                height={21}
                                className="mt-0.5 flex-shrink-0"
                              />
                              <div className="flex flex-col gap-0.5">
                                <span className="text-12--tamil-700">
                                  {item.label}:<br className="hidden sm:block" />
                                </span>
                                <span className="text-12-tamil-400">{item.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Third Card - Inauspicious Times */}
                <div className="flex flex-col justify-center p-2.5 sm:p-3 md:p-4 rounded">
                  {loading ? (
                    <div className="animate-pulse space-y-3 w-full">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`pt-1.5 sm:pt-2 ${i !== 1 ? "border-t border-FFFFFF/20" : ""}`}
                        >
                          <div className="h-3 bg-white/20 rounded w-1/3 mb-2"></div>
                          <div className="h-5 bg-white/20 rounded w-1/2"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {inauspiciousTimes.map((item, index) => (
                        <div
                          key={item.label}
                          className={`pt-1.5 sm:pt-2 ${index !== 0 ? "border-t border-FFFFFF/40" : ""
                            }`}
                        >
                          <p className="text-xs sm:text-sm text-FFFFFF text-14-tamil-400">
                            {item.label}
                          </p>
                          <p className="text-sm sm:text-base font-medium text-16-tamil-700">
                            {item.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Weekly/Monthly Images */}
          <div className="w-full xxl:w-[320px] p-4 xxl:p-6 flex flex-col justify-center">
            <div className="flex flex-row xxl:flex-col gap-10">
              {/* Weekly Image */}
              <div className="relative w-full h-auto flex-1">
                {weeklySlug ? (
                  <Link href={`/${weeklySlug}`} className="block hover:opacity-90 transition-opacity cursor-pointer">
                    <Image
                      src={weeklyImage}
                      alt="வார ராசிபலன்"
                      width={300}
                      height={120}
                      className="w-full h-auto object-cover"
                      style={{ boxShadow: '0 0 20px 2px #b34bd8', ...getFocusPointStyle(imageMetadata) }}
                      loading="lazy"
                      placeholder="blur"
                      blurDataURL={BLUR_PLACEHOLDER}
                    />
                  </Link>
                ) : (
                  <Image
                    src={weeklyImage}
                    alt="வார ராசிபலன்"
                    width={300}
                    height={120}
                    className="w-full h-auto object-cover"
                      style={{ boxShadow: '0 0 20px 2px #b34bd8', ...getFocusPointStyle(imageMetadata) }}
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL={BLUR_PLACEHOLDER}
                  />
                )}
              </div>

              {/* Monthly Image */}
              <div className="relative w-full h-auto flex-1">
                {monthlySlug ? (
                  <Link href={`/${monthlySlug}`} className="block hover:opacity-90 transition-opacity cursor-pointer">
                    <Image
                      src={monthlyImage}
                      alt="மாத ராசிபலன்"
                      width={300}
                      height={120}
                      className="w-full h-auto object-cover"
                      style={{ boxShadow: '0 0 20px 2px #b34bd8', ...getFocusPointStyle(imageMetadata) }}
                      loading="lazy"
                      placeholder="blur"
                      blurDataURL={BLUR_PLACEHOLDER}
                    />
                  </Link>
                ) : (
                  <Image
                    src={monthlyImage}
                    alt="மாத ராசிபலன்"
                    width={300}
                    height={120}
                    className="w-full h-auto object-cover"
                      style={{ boxShadow: '0 0 20px 2px #b34bd8', ...getFocusPointStyle(imageMetadata) }}
                    loading="lazy"
                    placeholder="blur"
                    blurDataURL={BLUR_PLACEHOLDER}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Astrology;
